import { GeofenceRequester, GrahpQLGeofenceRequester, LegacyGeofenceRequester } from '@cobliteam/api-lib';

import type { Geofence } from '@cobliteam/common-types/src/geofence';
import type { UUID } from '@cobliteam/common-types/src/identifier';
import { Environment } from '@cobliteam/react-environments';
import { QueryCache, type QueryKey, type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getGeofenceGraphQLRequester = () => new GrahpQLGeofenceRequester();
const getLegacyGeofenceRequester = () =>
  new LegacyGeofenceRequester({
    host: env.herbieApiHost,
  });
const getGeofenceRequester = () => new GeofenceRequester({ host: env.apiV2 });

const QUERY_KEY: QueryKey = ['GEOFENCES'];

const useGeofence = () => {
  const queryClient = useQueryClient();

  const refreshGeofencesCache = (geofenceId: UUID, updatedGeofence: Geofence) => {
    queryClient.setQueryData<Geofence[]>(QUERY_KEY, (old) => {
      if (old) {
        const geofenceToUpdateIndex = old.findIndex((geofence) => geofence.id === geofenceId);
        const updatedGeofences = [...old];
        updatedGeofences[geofenceToUpdateIndex] = {
          ...updatedGeofence,
        };

        return updatedGeofences;
      }
      return old;
    });
  };

  const GetGeofencesCache = () => {
    const queryCache = new QueryCache();
    return queryCache.find({
      queryKey: QUERY_KEY,
    });
  };

  const GetGeofences = (options?: Partial<UseQueryOptions<Geofence[]>>) => {
    return useQuery<Geofence[]>({
      queryKey: QUERY_KEY,
      queryFn: () => getGeofenceGraphQLRequester().getGeofences(),
      ...options,
    });
  };

  const CreateGeofence = useMutation({
    mutationFn: getGeofenceRequester().save,
    onSuccess: async (newGeofence) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      queryClient.setQueryData<Geofence[]>(QUERY_KEY, (old) => (old ? [...old, newGeofence] : [newGeofence]));
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const UpdateGeofence = useMutation({
    mutationFn: getGeofenceRequester().edit,
    onMutate: async (updatedGeofence) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previousGeofences: Geofence[] | undefined = queryClient.getQueryData(QUERY_KEY);

      refreshGeofencesCache(updatedGeofence.id, updatedGeofence);

      return { previousGeofences };
    },
    onSuccess: async (updatedGeofence, lastGeofence) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      refreshGeofencesCache(lastGeofence.id, updatedGeofence);
    },
    onError: (err, updatedGeofence, context) => {
      queryClient.setQueryData(QUERY_KEY, context?.previousGeofences);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const DeleteGeofence = useMutation({
    mutationFn: getGeofenceRequester().delete,
    onMutate: async (deletedGeofence) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previousGeofences: Geofence[] | undefined = queryClient.getQueryData(QUERY_KEY);

      queryClient.setQueryData<Geofence[]>(QUERY_KEY, (old) => {
        return old ? old.filter((geo) => geo.id !== deletedGeofence.id) : old;
      });

      return { previousGeofences };
    },
    onError: (err, deletedGeofence, context) => {
      queryClient.setQueryData(QUERY_KEY, context?.previousGeofences);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const ConfirmImportGeofence = useMutation({
    mutationFn: getLegacyGeofenceRequester().import,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const previousGeofences: Geofence[] | undefined = queryClient.getQueryData(QUERY_KEY);

      queryClient.invalidateQueries({ queryKey: QUERY_KEY });

      return { previousGeofences };
    },
    onError: (err, geofences, context) => {
      queryClient.setQueryData(QUERY_KEY, context?.previousGeofences);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return {
    GetGeofences,
    GetGeofencesCache,
    CreateGeofence,
    UpdateGeofence,
    DeleteGeofence,
    ConfirmImportGeofence,
  };
};
export { QUERY_KEY };
export default useGeofence;
